/**
 * Mock 데이터 저장소
 * 메모리 기반으로 동작하며, 페이지 새로고침 시 초기화됩니다.
 * 실제 API 연동 전까지 프론트엔드 개발에 사용됩니다.
 */

import { newsArticles, type NewsArticle } from '../newsData';
import { galleryItems, type GalleryItem } from '../galleryData';
import type {
  AdminUser,
  CreateNewsRequest,
  UpdateNewsRequest,
  CreateGalleryRequest,
  UpdateGalleryRequest,
  PaginationMeta,
} from '../../services/admin/types';

// Mock 관리자 계정
const MOCK_ADMIN: AdminUser = {
  id: 1,
  email: 'admin@church.com',
  name: '관리자',
  role: 'admin',
};

const MOCK_PASSWORD = 'admin123';

// 토큰 저장 키 (apiClient와 동일)
const TOKEN_KEY = 'admin_auth_token';

class MockStore {
  private news: NewsArticle[];
  private gallery: GalleryItem[];
  private nextNewsId: number;
  private nextGalleryId: number;

  constructor() {
    // 초기 데이터 복사 (원본 데이터 보존)
    this.news = [...newsArticles];
    this.gallery = [...galleryItems];
    this.nextNewsId = Math.max(...this.news.map((n) => n.id)) + 1;
    this.nextGalleryId = Math.max(...this.gallery.map((g) => g.id)) + 1;
  }

  // ==================== 인증 관련 ====================

  /**
   * localStorage에서 토큰 조회
   */
  private getStoredToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  /**
   * 토큰이 유효한 Mock 토큰인지 확인
   */
  private isValidMockToken(token: string | null): boolean {
    return !!token && token.startsWith('mock-token-');
  }

  /**
   * 로그인 처리
   */
  login(email: string, password: string): { token: string; user: AdminUser } | null {
    if (email === MOCK_ADMIN.email && password === MOCK_PASSWORD) {
      const token = `mock-token-${Date.now()}`;
      return { token, user: MOCK_ADMIN };
    }
    return null;
  }

  /**
   * 로그아웃 처리
   */
  logout(): void {
    // 토큰 삭제는 apiClient에서 처리
  }

  /**
   * 현재 사용자 정보 조회
   * localStorage의 토큰을 확인하여 사용자 정보 반환
   */
  getCurrentUser(): AdminUser | null {
    const token = this.getStoredToken();
    if (this.isValidMockToken(token)) {
      return MOCK_ADMIN;
    }
    return null;
  }

  /**
   * 토큰 유효성 검사
   */
  validateToken(token: string): boolean {
    return this.isValidMockToken(token);
  }

  // ==================== 뉴스 관련 ====================

  /**
   * 뉴스 목록 조회 (페이지네이션)
   */
  getNews(page: number = 1, limit: number = 10): { data: NewsArticle[]; pagination: PaginationMeta } {
    const totalItems = this.news.length;
    const totalPages = Math.ceil(totalItems / limit);
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;

    // 최신순 정렬
    const sortedNews = [...this.news].sort((a, b) => b.id - a.id);
    const data = sortedNews.slice(startIndex, endIndex);

    return {
      data,
      pagination: {
        currentPage: page,
        totalPages,
        totalItems,
        itemsPerPage: limit,
      },
    };
  }

  /**
   * 뉴스 상세 조회
   */
  getNewsById(id: number): NewsArticle | null {
    return this.news.find((n) => n.id === id) || null;
  }

  /**
   * 뉴스 생성
   */
  createNews(data: CreateNewsRequest): NewsArticle {
    const newNews: NewsArticle = {
      id: this.nextNewsId++,
      title: data.title,
      description: data.description,
      content: data.content,
      image: data.image,
      date: new Date().toLocaleDateString('ko-KR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }),
    };
    this.news.push(newNews);
    return newNews;
  }

  /**
   * 뉴스 수정
   */
  updateNews(data: UpdateNewsRequest): NewsArticle | null {
    const index = this.news.findIndex((n) => n.id === data.id);
    if (index === -1) return null;

    this.news[index] = {
      ...this.news[index],
      title: data.title,
      description: data.description,
      content: data.content,
      image: data.image,
    };
    return this.news[index];
  }

  /**
   * 뉴스 삭제
   */
  deleteNews(id: number): boolean {
    const index = this.news.findIndex((n) => n.id === id);
    if (index === -1) return false;

    this.news.splice(index, 1);
    return true;
  }

  // ==================== 갤러리 관련 ====================

  /**
   * 갤러리 목록 조회 (페이지네이션, 카테고리 필터)
   */
  getGallery(
    page: number = 1,
    limit: number = 10,
    category?: string
  ): { data: GalleryItem[]; pagination: PaginationMeta } {
    // 카테고리 필터링
    let filteredGallery = this.gallery;
    if (category && category !== 'all') {
      filteredGallery = this.gallery.filter((g) => g.category === category);
    }

    const totalItems = filteredGallery.length;
    const totalPages = Math.ceil(totalItems / limit);
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;

    // 최신순 정렬
    const sortedGallery = [...filteredGallery].sort((a, b) => b.id - a.id);
    const data = sortedGallery.slice(startIndex, endIndex);

    return {
      data,
      pagination: {
        currentPage: page,
        totalPages,
        totalItems,
        itemsPerPage: limit,
      },
    };
  }

  /**
   * 갤러리 상세 조회
   */
  getGalleryById(id: number): GalleryItem | null {
    return this.gallery.find((g) => g.id === id) || null;
  }

  /**
   * 갤러리 생성
   */
  createGallery(data: CreateGalleryRequest): GalleryItem {
    const newGallery: GalleryItem = {
      id: this.nextGalleryId++,
      title: data.title,
      category: data.category,
      images: data.images,
      date: new Date().toLocaleDateString('ko-KR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }),
    };
    this.gallery.push(newGallery);
    return newGallery;
  }

  /**
   * 갤러리 수정
   */
  updateGallery(data: UpdateGalleryRequest): GalleryItem | null {
    const index = this.gallery.findIndex((g) => g.id === data.id);
    if (index === -1) return null;

    this.gallery[index] = {
      ...this.gallery[index],
      title: data.title,
      category: data.category,
      images: data.images,
    };
    return this.gallery[index];
  }

  /**
   * 갤러리 삭제
   */
  deleteGallery(id: number): boolean {
    const index = this.gallery.findIndex((g) => g.id === id);
    if (index === -1) return false;

    this.gallery.splice(index, 1);
    return true;
  }

  // ==================== 유틸리티 ====================

  /**
   * 데이터 초기화 (테스트용)
   */
  reset(): void {
    this.news = [...newsArticles];
    this.gallery = [...galleryItems];
    this.nextNewsId = Math.max(...this.news.map((n) => n.id)) + 1;
    this.nextGalleryId = Math.max(...this.gallery.map((g) => g.id)) + 1;
  }
}

// 싱글톤 인스턴스 내보내기
export const mockStore = new MockStore();
